/* eslint-disable @typescript-eslint/no-explicit-any */
import { RUNTIME } from './runtimeEnv';

const ALLOWED_ORGS = new Set(['bomet', 'nyandarua', 'baringo', 'lims']);

function normalizeOrg(v?: string | null) {
  if (!v) return undefined;
  const k = v.toLowerCase();

  return ALLOWED_ORGS.has(k) ? k : undefined;
}

const runtimeOrg = RUNTIME.ORG;
const runtimeBaseApi = RUNTIME.BASE_API;
const runtimeBasePath = RUNTIME.BASE_PATH;

const BASE_ENV = (runtimeBasePath || '').trim();
const BASE_TPL = BASE_ENV;
const tplHasOrg = !!BASE_TPL && BASE_TPL.includes(':org');

function buildOrgRegexFromTemplate(): RegExp | null {
  if (!BASE_TPL || !tplHasOrg) return null;
  const esc = BASE_TPL.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&').replace(
    /\\:org/g,
    '([^/]+)'
  );

  return new RegExp(`(?:^|/)${esc}(?:/|$)`, 'i');
}

const ORG_IN_PATH_RE = buildOrgRegexFromTemplate();

const trim = (s: string) => s.replace(/^\/+/, '').replace(/\/+$/, '');
const cleanSlashes = (s: string) => s.replace(/\/{2,}/g, '/');

function isAbsoluteHttpUrl(u?: string): u is string {
  if (!u) return false;

  try {
    const url = new URL(u);

    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function joinAbsUrl(base: string, path: string): string {
  const b = base.replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;

  return `${b}${p}`;
}

export function joinUrl(...parts: Array<string | undefined | null>) {
  const filtered = parts.filter(Boolean) as string[];

  if (filtered.length === 0) return '/';
  const first = filtered[0]!;
  const isAbsBase = /^https?:\/\//i.test(first);

  const normalized = filtered.map((seg, i) => {
    if (i === 0) {
      return isAbsBase
        ? seg.replace(/\/+$/, '')
        : seg.replace(/^\/+|\/+$/g, '');
    }

    return seg.replace(/^\/+|\/+$/g, '');
  });

  const joined = normalized.join('/');

  if (isAbsBase) return joined;

  return joined.startsWith('/') ? joined : `/${joined}`;
}

function deriveOrgFromConcreteBase(): string | undefined {
  if (!BASE_TPL || tplHasOrg) return undefined;
  const m = /(?:^|\/)dev\/([^/]+)\/public(?:\/|$)/i.exec(BASE_TPL);

  return normalizeOrg(m?.[1]);
}

const DEFAULT_FALLBACK_ORG =
  normalizeOrg(runtimeOrg) ??
  normalizeOrg(process.env.NEXT_PUBLIC_ORG) ??
  'nyandarua';

export function getRuntimeOrg(): string {
  if (typeof window !== 'undefined' && ORG_IN_PATH_RE) {
    const m = ORG_IN_PATH_RE.exec(window.location.pathname);
    const pathOrg = normalizeOrg(m?.[1]);

    if (pathOrg) {
      return pathOrg;
    }
  }

  const fromBase = deriveOrgFromConcreteBase();

  if (fromBase) return fromBase;

  const fromRuntime = normalizeOrg(runtimeOrg);

  if (fromRuntime) return fromRuntime;

  return DEFAULT_FALLBACK_ORG;
}

export function getBasePath(org?: string) {
  const o = normalizeOrg(org) || getRuntimeOrg();

  if (!BASE_TPL || BASE_TPL === '/') return '';
  const rendered = tplHasOrg ? BASE_TPL.replace(':org', o) : BASE_TPL;
  const cleaned = '/' + trim(rendered);

  return cleaned === '/' ? '' : cleaned;
}

export function getRuntimePaybill(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const v = (window as any).__RUNTIME_CONFIG__?.NEXT_PUBLIC_PAYBILL_NUMBER;
  const s = typeof v === 'string' ? v.trim() : '';
  return s || undefined;
}


export function getRuntimeBankName(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const v = (window as any).__RUNTIME_CONFIG__?.NEXT_PUBLIC_BANK_NAME;
  const s = typeof v === 'string' ? v.trim() : '';
  return s || undefined;
}

export function getRuntimeBankAccountName(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const v = (window as any).__RUNTIME_CONFIG__?.NEXT_PUBLIC_BANK_ACCOUNT_NAME;
  const s = typeof v === 'string' ? v.trim() : '';
  return s || undefined;
}

export function getRuntimeBankAccountNumber(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const v = (window as any).__RUNTIME_CONFIG__?.NEXT_PUBLIC_BANK_ACCOUNT_NUMBER;
  const s = typeof v === 'string' ? v.trim() : '';
  return s || undefined;
}



export function getServerBasePath(org?: string) {
  const o =
    normalizeOrg(org) ||
    normalizeOrg(process.env.NEXT_PUBLIC_ORG) ||
    'nyandarua';
  const baseTpl = (process.env.NEXT_PUBLIC_BASE_PATH || '').trim();
  const hasOrg = !!baseTpl && baseTpl.includes(':org');

  if (!baseTpl || baseTpl === '/') return '';
  const rendered = hasOrg ? baseTpl.replace(':org', o) : baseTpl;
  const cleaned = '/' + trim(rendered);

  return cleaned === '/' ? '' : cleaned;
}

export function getBaseApi(): string {
  const picked =
    (typeof window !== 'undefined'
      ? runtimeBaseApi
      : process.env.NEXT_PUBLIC_BASE_API) || '';

  if (isAbsoluteHttpUrl(picked)) return picked.replace(/\/+$/, '');

  if (process.env.NODE_ENV !== 'production') {
    const devDefault = '';

    console.warn(
      '[runtime] BASE_API missing/invalid; dev fallback ->',
      devDefault
    );

    return devDefault;
  }

  throw new Error(
    '[runtime] NEXT_PUBLIC_BASE_API is missing or invalid (must be absolute http/https URL).'
  );
}

export function getServerBaseApi(): string {
  const env = process.env.NEXT_PUBLIC_BASE_API || '';

  if (isAbsoluteHttpUrl(env)) return env.replace(/\/+$/, '');

  if (process.env.NODE_ENV !== 'production') {
    return '';
  }

  throw new Error('[runtime] Server BASE_API invalid/missing');
}

export function getAppBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin + (getBasePath() || '');
  }

  const appOrigin = process.env.NEXT_PUBLIC_APP_ORIGIN;

  if (appOrigin && isAbsoluteHttpUrl(appOrigin)) {
    return appOrigin.replace(/\/+$/, '') + (getServerBasePath() || '');
  }

  return getServerBaseApi().replace(/\/+$/, '') + (getServerBasePath() || '');
}

function stripLeading(str: string, lead: string): string {
  if (!lead) return str;
  const a = '/' + trim(lead);
  const b = '/' + trim(str);

  return b.startsWith(a + '/') ? b.slice(a.length) : b === a ? '/' : b;
}

function ensureOrgPrefix(path: string, org: string): string {
  const p = trim(path);

  if (p === org || p.startsWith(`${org}/`)) return p;

  return `${org}/${p}`;
}

export function asset(path: string, org?: string) {
  if (isAbsoluteHttpUrl(path)) return path;
  const o = normalizeOrg(org) || getRuntimeOrg();
  const base = getBasePath(o);
  let p = trim(path);

  if (base) p = trim(stripLeading('/' + p, base));
  p = ensureOrgPrefix(p, o);

  return base ? joinUrl(base, p) : '/' + cleanSlashes(p);
}

export function imageSrc(path: string, org?: string) {
  if (isAbsoluteHttpUrl(path)) return path;
  const o = normalizeOrg(org) || getRuntimeOrg();
  const base = getBasePath(o);
  let p = trim(path);

  if (base) p = trim(stripLeading('/' + p, base));
  p = ensureOrgPrefix(p, o);

  return '/' + cleanSlashes(p);
}

export function assetPublic(path: string) {
  if (isAbsoluteHttpUrl(path)) return path;
  const base = getBasePath();
  const p = trim(path);
  const normalized = base ? trim(stripLeading('/' + p, base)) : p;

  return base ? joinUrl(base, normalized) : '/' + normalized;
}

export function hasBasePath(): boolean {
  const basePath =
    typeof window !== 'undefined' ? getBasePath() : getServerBasePath();

  return !!basePath && basePath !== '/';
}

export function getAllowedOrgs(): string[] {
  return Array.from(ALLOWED_ORGS);
}

export function isValidOrg(org: string): boolean {
  return ALLOWED_ORGS.has(org.toLowerCase());
}

export function createRedirectUrl(path: string, org?: string): string {
  const targetOrg = org || getRuntimeOrg();
  const base = getBasePath(targetOrg);
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const normalized = base ? stripLeading(cleanPath, base) : cleanPath;

  return base ? `${base}${normalized}` : normalized;
}

export function useCurrentOrg(): string {
  if (typeof window === 'undefined') {
    throw new Error('useCurrentOrg can only be used in client components');
  }

  return getRuntimeOrg();
}

export function stripBase(pathname: string): string {
  const baseTpl = (
    typeof window !== 'undefined'
      ? runtimeBasePath || ''
      : process.env.NEXT_PUBLIC_BASE_PATH || ''
  ).trim();

  if (!baseTpl || baseTpl === '/') return pathname || '/';

  const pattern = baseTpl
    .replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
    .replace(/\\:org/g, '[^/]+');

  const re = new RegExp(`^(?:.*?)${pattern}(\\/.*)?$`, 'i');
  const m = re.exec(pathname);

  return m ? m[1] || '/' : pathname || '/';
}

export function getServerRuntimeOrg(): string {
  const fromRuntime = normalizeOrg(runtimeOrg);

  if (fromRuntime) return fromRuntime;

  const fromEnv = normalizeOrg(process.env.NEXT_PUBLIC_ORG || '');

  if (fromEnv) return fromEnv;

  return 'nyandarua';
}
